import {
  ChangeEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useStateVar } from 'helpers/useStateVar';
import { Flex } from 'reflexbox';
import {
  TextLine, CustomSelect,
} from './styles';
import { useTranslation } from 'react-i18next';

import {
  Input, Checkbox, Button,
  InputCalculator,
} from 'components';

import {
  ToggleSwitchMini,
} from '../ToggleSwitch';
import {
  ComponentProps, ModoEcoData, ModoSobDemandaData,
} from './types';
import { t } from 'i18next';
import { ModoSobDemanda } from './components/modoSobDemanda';
import { ModoEco } from './components/modoEco';
import {
  parseScheduleData, validateSchedule, setpointFromActionCmd,
  actionPostModeFromCmd,
} from './constants';
import { toast } from 'react-toastify';
import { ScheduleDut4 } from '~/providers/types/api-private';
import { colors } from '~/styles/colors';

const simpleActionModesOptions = [
  { label: t('refrigerar'), value: '1_REFRIGERAR' },
  { label: t('desligar'), value: '0_DESLIGAR' },
  { label: t('ventilar'), value: '2_VENTILAR' },
];

const advancedActionModesOptions = [
  { label: t('modoSobDemanda'), value: '3_SOB_DEMANDA' },
  { label: t('modoEco'), value: '4_ECO' },
];

const DEFAULT_SETPOINT_VALUES = [16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30];

function numToStr(value: number | null | undefined): string {
  return value != null ? value.toString() : '';
}

export const ScheduleEditCardDut4 = (props: ComponentProps): JSX.Element => {
  const { t } = useTranslation();

  const [state, render, setState] = useStateVar({
    // Objeto que será retornado para criar a programação
    formData: {
      DUT_SCHEDULE_ID: props.schedule?.DUT_SCHEDULE_ID || null,
      SCHEDULE_TITLE: props.schedule?.SCHEDULE_TITLE || null,
      SCHEDULE_STATUS: props.schedule != null ? props.schedule.SCHEDULE_STATUS : true,
      BEGIN_TIME: props.schedule?.BEGIN_TIME || null,
      END_TIME: props.schedule?.END_TIME || null,
      CTRLOPER: props.schedule?.CTRLOPER || null,
      DAYS: {
        mon: props.schedule?.DAYS.mon != null ? props.schedule.DAYS.mon : true,
        tue: props.schedule?.DAYS.tue != null ? props.schedule.DAYS.tue : true,
        wed: props.schedule?.DAYS.wed != null ? props.schedule.DAYS.wed : true,
        thu: props.schedule?.DAYS.thu != null ? props.schedule.DAYS.thu : true,
        fri: props.schedule?.DAYS.fri != null ? props.schedule.DAYS.fri : true,
        sat: props.schedule?.DAYS.sat != null ? props.schedule.DAYS.sat : false,
        sun: props.schedule?.DAYS.sun != null ? props.schedule.DAYS.sun : false,
      },
      SETPOINT: props.action === 'create'
        ? props.irCommands?.[0]?.TEMPER || '22'
        : props.schedule?.SETPOINT || '22',
    } as ScheduleDut4,
    allDay: props.schedule ? props.schedule.BEGIN_TIME === '00:00' && props.schedule.END_TIME === '23:59' : false as boolean,
  });

  const [modeData, setModeData] = useState({
    type: '' as 'eco' | 'modoSobDemanda',
    sobDemanda: {} as ModoSobDemandaData,
    eco: {} as ModoEcoData,
  });

  const advancedModes = ['3_SOB_DEMANDA', '4_ECO'];
  const [modoAvancado, setModoAvancado] = useState(
    advancedModes.includes(props.schedule?.CTRLOPER ?? ''),
  );
  const actionModesOptions = modoAvancado ? advancedActionModesOptions : simpleActionModesOptions;

  const [open, setOpen] = useState(false);
  const [height, setHeight] = useState(0);
  const innerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!innerRef.current) return;

    if (!open) {
      setHeight(0);
      return;
    }

    setHeight(innerRef.current.scrollHeight);

    const observer = new ResizeObserver(() => {
      if (innerRef.current) {
        setHeight(innerRef.current.scrollHeight);
      }
    });

    observer.observe(innerRef.current);
    return () => observer.disconnect();
  }, [open]);

  function setFormData(obj, withRender = true) {
    Object.assign(state.formData, obj);

    if (obj.hasOwnProperty('BEGIN_TIME') || obj.hasOwnProperty('END_TIME')) {
      state.allDay = state.formData.BEGIN_TIME === '00:00' && state.formData.END_TIME === '23:59';
    }

    if (withRender) {
      render();
    }
  }

  function setFormDataDays(obj) {
    Object.assign(state.formData.DAYS, obj);
    render();
  }

  function increaseDecrease(value: number) {
    state.formData.SETPOINT = value;
    render();
  }

  function handleTimeChange(field: 'BEGIN_TIME' | 'END_TIME', value: string): void {
    if (!/^\d{2}:\d{2}$/.test(value)) {
      setFormData({ [field]: value });
      return;
    }

    const [hoursRaw, minutes] = value.split(':').map(Number);
    const hours = Math.min(hoursRaw, 23);

    let formattedValue = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

    if (field === 'END_TIME' && formattedValue === '00:00') {
      formattedValue = '23:59';
    }

    setFormData({ [field]: formattedValue });
  }

  useEffect(() => {
    props.onChange?.(modeData);
  }, [modeData]);

  const modesRequiringIr = ['1_REFRIGERAR', '3_SOB_DEMANDA', '4_ECO'];

  function createOrEditSchedule() {
    const timeRegex = /^\d{2}:\d{2}$/;

    if (!state.formData.SCHEDULE_TITLE?.trim()) {
      toast.error(t('erroTituloProgramacaoNecessario'));
      return;
    }

    if (!timeRegex.test(state.formData.BEGIN_TIME ?? '')) {
      toast.error(t('erroHorarioInicioObrigatorio'));
      return;
    }

    if (!timeRegex.test(state.formData.END_TIME ?? '')) {
      toast.error(t('erroHorarioFimObrigatorio'));
      return;
    }

    if (state.formData.BEGIN_TIME! >= state.formData.END_TIME!) {
      toast.error(t('erroHorarioInicioMenorHorarioFim'));
      return;
    }

    if (
      !props.isMultipleDevProg
      && props.irCommands.length === 0
      && modesRequiringIr.includes(state.formData.CTRLOPER ?? '')
    ) {
      toast.error(t('erroModoPrecisaComandoIr'));
      return;
    }

    if (!props.isMultipleDevProg && state.formData.CTRLOPER === '3_SOB_DEMANDA' && props.irCommands.length > 0) {
      const availableTemps = props.irCommands.map((cmd) => cmd.TEMPER);
      const sobDemanda = modeData.sobDemanda;

      if (sobDemanda.actionMode === 'COOL' && (!sobDemanda.setpoint || !availableTemps.includes(Number(sobDemanda.setpoint)))) {
        toast.error(t('erroModoPrecisaComandoIr'));
        return;
      }

      if (sobDemanda.actionPostMode === 'COOL' && (!sobDemanda.actionPostSetpoint || !availableTemps.includes(Number(sobDemanda.actionPostSetpoint)))) {
        toast.error(t('erroModoPrecisaComandoIr'));
        return;
      }

      if ((sobDemanda.lti_cmd === 'FAN' || sobDemanda.lti_cmd === 'OFF') && sobDemanda.lti_degree && !availableTemps.includes(Number(sobDemanda.lti_degree))) {
        toast.error(t('erroModoPrecisaComandoIr'));
        return;
      }
    }

    if (!props.isMultipleDevProg && state.formData.CTRLOPER === '4_ECO' && props.irCommands.length > 0) {
      const availableTemps = props.irCommands.map((cmd) => cmd.TEMPER);
      const eco = modeData.eco;

      if (!eco.setpoint || !availableTemps.includes(Number(eco.setpoint))) {
        toast.error(t('erroModoPrecisaComandoIr'));
        return;
      }

      if ((eco.lti_cmd === 'FAN' || eco.lti_cmd === 'OFF') && eco.lti_degree && !availableTemps.includes(Number(eco.lti_degree))) {
        toast.error(t('erroModoPrecisaComandoIr'));
        return;
      }
    }

    const ctrloper = state.formData.CTRLOPER;
    const effectiveType: 'modoSobDemanda' | 'eco' | '' = ctrloper === '3_SOB_DEMANDA' ? 'modoSobDemanda' : ctrloper === '4_ECO' ? 'eco' : '';

    const error = validateSchedule(
      effectiveType,
      effectiveType === 'eco' ? modeData.eco : modeData.sobDemanda,
    );

    if (error) {
      toast.error(error);
      return;
    }

    if (state.formData.CTRLOPER) {
      const data = parseScheduleData(
        state.formData.SCHEDULE_TITLE,
        state.formData.SCHEDULE_STATUS,
        state.formData.CTRLOPER,
        state.formData.DAYS,
        state.formData.BEGIN_TIME,
        state.formData.END_TIME,
        modeData.type === 'eco' ? modeData.eco
          : modeData.sobDemanda,
        state.formData.SETPOINT,
        state.formData.DUT_SCHEDULE_ID,
      );

      props.onHandleSave(data);
    }
  }

  return (
    <Flex
      flexWrap="nowrap"
      flexDirection="column"
      width="452px"
      style={{
        borderTop: `15px solid ${colors.brand.principal}`,
        borderRadius: '5px',
        marginBottom: '63px',
      }}
    >
      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{
          marginTop: '40px',
          marginLeft: '27px',
        }}
      >
        <div
          style={{
            fontSize: '15px',
            fontWeight: 'bold',
          }}
        >
          {t('adicionarProgramacao')}
        </div>
      </Flex>
      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{
          marginTop: '13px',
          marginLeft: '27px',
        }}
      >
        <div
          style={{
            width: '145px',
            fontSize: '12px',
            fontWeight: 'bold',
          }}
        >
          {t('titulo')}
        </div>
      </Flex>
      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{
          marginTop: '7px',
          marginLeft: '27px',
        }}
      >
        <TextLine style={{ marginBottom: '20px', width: '210px' }}>
          <Input
            data-testid="schedule-dut4-title-input"
            type="text"
            value={state.formData.SCHEDULE_TITLE}
            label=""
            onChange={(e: ChangeEvent<HTMLInputElement>) => { setFormData({ SCHEDULE_TITLE: e.target.value }); render(); }}
          />
        </TextLine>
        <Flex
          flexWrap="nowrap"
          flexDirection="column"
          style={{
            marginLeft: '20px',
          }}
        >
          <div
            style={{
              fontSize: '12px',
              fontWeight: 'bold',
              color: colors.GreyDark,
            }}
          >
            {t('Programação')}
          </div>
          <Flex
            flexWrap="nowrap"
            flexDirection="row"
            alignItems="center"
            style={{
              gap: '8px',
              borderRadius: '20px',
              width: 'fit-content',
            }}
          >
            <span style={{
              fontSize: '11px',
              color: !state.formData.SCHEDULE_STATUS ? colors.Blue300 : colors.Grey200,
              fontWeight: !state.formData.SCHEDULE_STATUS ? 'bold' : 'normal',
              transition: 'color 0.2s',
            }}
            >
              {t('desabilitada')}
            </span>
            <ToggleSwitchMini
              dataTestId="schedule-dut4-status-toggle"
              checked={state.formData.SCHEDULE_STATUS}
              onOff
              onClick={(e) => { e.preventDefault(); setFormData({ SCHEDULE_STATUS: !state.formData.SCHEDULE_STATUS }); }}
            />
            <span style={{
              fontSize: '11px',
              color: !state.formData.SCHEDULE_STATUS ? colors.Grey200 : colors.Blue300,
              fontWeight: !state.formData.SCHEDULE_STATUS ? 'normal' : 'bold',
              transition: 'color 0.2s',
            }}
            >
              {t('habilitada')}
            </span>
          </Flex>
        </Flex>
      </Flex>
      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{
          marginTop: '15px',
          marginLeft: '27px',
        }}
      >
        <div
          style={{
            width: '145px',
            fontSize: '12px',
            fontWeight: 'bold',
          }}
        >
          {t('selecioneDias')}
        </div>
      </Flex>
      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{
          marginLeft: '57px',
          marginTop: '13px',
        }}
      >
        <div
          style={{
            width: '34px',
            height: '37px',
            borderRadius: '5px',
            backgroundColor: state.formData.DAYS.sun ? colors.brand.principal : colors.GreyBorderSoft,
          }}
        >
          <div
            data-testid="day-sun"
            data-selected={String(state.formData.DAYS.sun)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              marginTop: '11px',
              color: state.formData.DAYS.sun ? colors.White : colors.Black,
              cursor: 'pointer',
            }}
            onClick={(e) => { e.preventDefault(); setFormDataDays({ sun: !state.formData.DAYS.sun }); }}
          >
            {t('diaDom').toLocaleUpperCase()}
          </div>
        </div>
        <div
          style={{
            width: '34px',
            height: '37px',
            borderRadius: '5px',
            marginLeft: '10px',
            backgroundColor: state.formData.DAYS.mon ? colors.brand.principal : colors.GreyBorderSoft,
          }}
        >
          <div
            data-testid="day-mon"
            data-selected={String(state.formData.DAYS.mon)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              marginTop: '11px',
              color: state.formData.DAYS.mon ? colors.White : colors.Black,
              cursor: 'pointer',
            }}
            onClick={(e) => { e.preventDefault(); setFormDataDays({ mon: !state.formData.DAYS.mon }); }}
          >
            {t('diaSeg').toLocaleUpperCase()}
          </div>
        </div>
        <div
          style={{
            width: '34px',
            height: '37px',
            borderRadius: '5px',
            marginLeft: '10px',
            backgroundColor: state.formData.DAYS.tue ? colors.brand.principal : colors.GreyBorderSoft,
          }}
        >
          <div
            data-testid="day-tue"
            data-selected={String(state.formData.DAYS.tue)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              marginTop: '11px',
              color: state.formData.DAYS.tue ? colors.White : colors.Black,
              cursor: 'pointer',
            }}
            onClick={(e) => { e.preventDefault(); setFormDataDays({ tue: !state.formData.DAYS.tue }); }}
          >
            {t('diaTer').toLocaleUpperCase()}
          </div>
        </div>
        <div
          style={{
            width: '34px',
            height: '37px',
            borderRadius: '5px',
            marginLeft: '10px',
            backgroundColor: state.formData.DAYS.wed ? colors.brand.principal : colors.GreyBorderSoft,
          }}
        >
          <div
            data-testid="day-wed"
            data-selected={String(state.formData.DAYS.wed)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              marginTop: '11px',
              color: state.formData.DAYS.wed ? colors.White : colors.Black,
              cursor: 'pointer',
            }}
            onClick={(e) => { e.preventDefault(); setFormDataDays({ wed: !state.formData.DAYS.wed }); }}
          >
            {t('diaQua').toLocaleUpperCase()}
          </div>
        </div>
        <div
          style={{
            width: '34px',
            height: '37px',
            borderRadius: '5px',
            marginLeft: '10px',
            backgroundColor: state.formData.DAYS.thu ? colors.brand.principal : colors.GreyBorderSoft,
          }}
        >
          <div
            data-testid="day-thu"
            data-selected={String(state.formData.DAYS.thu)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              marginTop: '11px',
              color: state.formData.DAYS.thu ? colors.White : colors.Black,
              cursor: 'pointer',
            }}
            onClick={(e) => { e.preventDefault(); setFormDataDays({ thu: !state.formData.DAYS.thu }); }}
          >
            {t('diaQui').toLocaleUpperCase()}
          </div>
        </div>
        <div
          style={{
            width: '34px',
            height: '37px',
            borderRadius: '5px',
            marginLeft: '10px',
            backgroundColor: state.formData.DAYS.fri ? colors.brand.principal : colors.GreyBorderSoft,
          }}
        >
          <div
            data-testid="day-fri"
            data-selected={String(state.formData.DAYS.fri)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              marginTop: '11px',
              color: state.formData.DAYS.fri ? colors.White : colors.Black,
              cursor: 'pointer',
            }}
            onClick={(e) => { e.preventDefault(); setFormDataDays({ fri: !state.formData.DAYS.fri }); }}
          >
            {t('diaSex').toLocaleUpperCase()}
          </div>
        </div>
        <div
          style={{
            width: '34px',
            height: '37px',
            borderRadius: '5px',
            marginLeft: '10px',
            backgroundColor: state.formData.DAYS.sat ? colors.brand.principal : colors.GreyBorderSoft,
          }}
        >
          <div
            data-testid="day-sat"
            data-selected={String(state.formData.DAYS.sat)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              marginTop: '11px',
              color: state.formData.DAYS.sat ? colors.White : colors.Black,
              cursor: 'pointer',
            }}
            onClick={(e) => { e.preventDefault(); setFormDataDays({ sat: !state.formData.DAYS.sat }); }}
          >
            {t('diaSab').toLocaleUpperCase()}
          </div>
        </div>
      </Flex>
      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{
          marginTop: '13px',
          marginLeft: '27px',
        }}
      >
        <div
          style={{
            fontSize: '12px',
            fontWeight: 'bold',
          }}
        >
          {t('horarioInicio')}
        </div>
        <div
          style={{
            fontSize: '12px',
            fontWeight: 'bold',
            marginLeft: '60px',
          }}
        >
          {t('horarioFim')}
        </div>
      </Flex>
      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{
          marginTop: '13px',
          marginLeft: '27px',
        }}
      >
        <TextLine style={{ marginBottom: '20px', width: '112px' }}>
          <Input
            data-testid="begin-time-input"
            value={state.formData.BEGIN_TIME}
            mask={[/[0-2]/, /\d/, ':', /[0-5]/, /\d/]}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleTimeChange('BEGIN_TIME', e.target.value)}
          />
        </TextLine>
        <TextLine style={{ marginBottom: '20px', width: '112px', marginLeft: '34px' }}>
          <Input
            data-testid="end-time-input"
            value={state.formData.END_TIME}
            mask={[/[0-2]/, /\d/, ':', /[0-5]/, /\d/]}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleTimeChange('END_TIME', e.target.value)}
          />
        </TextLine>
        <div
          style={{
            marginLeft: '26px',
            marginTop: '14px',
          }}
        >
          <label
            onClick={() => {
              state.allDay = !state.allDay;

              if (state.allDay) {
                setFormData({ BEGIN_TIME: '00:00', END_TIME: '23:59' });
              }
              else {
                setFormData({ BEGIN_TIME: null, END_TIME: null });
              }
            }}
          >
            <Checkbox data-testid="schedule-dut4-all-day-checkbox" checked={state.allDay} />
          </label>
        </div>
        <div
          style={{
            fontSize: '12px',
            fontWeight: 'bold',
            marginTop: '16px',
            marginLeft: '8px',
          }}
        >
          {t('diaInteiro')}
        </div>
      </Flex>
      <Flex
        flexWrap="nowrap"
        flexDirection="column"
        style={{
          marginTop: '15px',
          marginLeft: '27px',
          gap: '8px',
        }}
      >
        <div
          style={{
            fontSize: '12px',
            fontWeight: 'bold',
          }}
        >
          {t('funcionamento')}
        </div>
        <Flex
          flexWrap="nowrap"
          flexDirection="row"
          alignItems="center"
          style={{
            gap: '8px',
            borderRadius: '20px',
            width: 'fit-content',
          }}
        >
          <span style={{
            fontSize: '11px',
            color: modoAvancado ? colors.Grey200 : colors.Blue300,
            fontWeight: modoAvancado ? 'normal' : 'bold',
            transition: 'color 0.2s',
          }}
          >
            {t('modoSimples')}
          </span>
          <ToggleSwitchMini
            dataTestId="schedule-dut4-modo-avancado-toggle"
            checked={modoAvancado}
            onClick={(e) => {
              e.preventDefault();
              const next = !modoAvancado;
              setModoAvancado(next);
              if (!next && advancedModes.includes(state.formData.CTRLOPER ?? '')) {
                setState({ formData: { ...state.formData, CTRLOPER: null, SETPOINT: undefined } });
                setModeData({ type: '' as 'eco' | 'modoSobDemanda', sobDemanda: {} as ModoSobDemandaData, eco: {} as ModoEcoData });
              }
            }}
          />
          <span style={{
            fontSize: '11px',
            color: modoAvancado ? colors.Blue300 : colors.Grey200,
            fontWeight: modoAvancado ? 'bold' : 'normal',
            transition: 'color 0.2s',
          }}
          >
            {t('modoAvancado')}
          </span>
        </Flex>
      </Flex>
      <div>
        <Flex
          flexWrap="nowrap"
          flexDirection="row"
          style={{
            marginTop: '10px',
            marginLeft: '24px',
          }}
        >
          <CustomSelect
            data-testid="schedule-dut4-action-mode-select"
            options={actionModesOptions}
            value={
                actionModesOptions.find((option) => option.value === state.formData.CTRLOPER)?.label || ''
              }
            placeholder={t('modoDeOperacao')}
            onSelect={(item) => {
              const modesWithoutFields = ['0_DESLIGAR', '2_VENTILAR'];
              const clearFields = modesWithoutFields.includes(item.value);
              let setpoint: number | undefined = state.formData.SETPOINT;
              if (clearFields) setpoint = undefined;
              else if (item.value === '1_REFRIGERAR') setpoint = props.irCommands?.[0]?.TEMPER ?? state.formData.SETPOINT;
              if (clearFields) {
                setModeData({ type: '' as 'eco' | 'modoSobDemanda', sobDemanda: {} as ModoSobDemandaData, eco: {} as ModoEcoData });
              }
              setState({
                formData: {
                  ...state.formData,
                  CTRLOPER: item.value,
                  SETPOINT: setpoint,
                },
              });
            }}
            style={state.formData.CTRLOPER === '1_REFRIGERAR' ? { width: '188px' } : { width: '400px' }}
            notNull
          />
          {state.formData.CTRLOPER === '1_REFRIGERAR' && (
            <InputCalculator
              data-testid="schedule-dut4-setpoint-input"
              label={t('setpointC')}
              value={state.formData.SETPOINT ? state.formData.SETPOINT.toString() : ''}
              possibleValues={props.isMultipleDevProg ? DEFAULT_SETPOINT_VALUES : props.irCommands.map((command) => command.TEMPER)}
              unity="ºC"
              onIncreaseDecrease={increaseDecrease}
              isSetpoint
              disabled={!props.isMultipleDevProg && props.irCommands.length === 0}
              style={{ height: '50px', marginRight: '24px' }}
            />
          )}
        </Flex>
      </div>
      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{
          marginTop: '10px',
          marginLeft: '24px',
          marginRight: '24px',
        }}
      >
        {
        state.formData.CTRLOPER === '3_SOB_DEMANDA' && (
          <ModoSobDemanda
            irCommands={props.irCommands}
            isMultipleDevProg={props.isMultipleDevProg}
            onChange={(data) => setModeData((prev) => ({ ...prev, sobDemanda: data, type: 'modoSobDemanda' }))}
            initialData={props.action === 'edit' && props.schedule?.CTRLOPER === '3_SOB_DEMANDA' ? {
              driveTemperature: numToStr(props.schedule.TEMP_TRIGGER),
              actionMode: props.schedule.SP_ACTION ?? null,
              setpoint: setpointFromActionCmd(props.schedule.SP_CMD),
              actionTime: props.schedule.SP_INTV?.toString() ?? '5',
              actionPostMode: actionPostModeFromCmd(props.schedule.SP_END_CMD),
              actionPostSetpoint: setpointFromActionCmd(props.schedule.SP_END_CMD),
              lti_cmd: props.schedule.LTI_CMD?.toString() ?? 'NO_ACTION',
              lti_degree: numToStr(props.schedule.LTI),
              upperHysteresis: numToStr(props.schedule.UPPER_HYSTERESIS),
              lowerHysteresis: numToStr(props.schedule.LOWER_HYSTERESIS),
            } : undefined}
          />
        )
      }
        {
        state.formData.CTRLOPER === '4_ECO' && (
          <ModoEco
            irCommands={props.irCommands}
            isMultipleDevProg={props.isMultipleDevProg}
            onChange={(data) => setModeData((prev) => ({ ...prev, eco: data, type: 'eco' }))}
            initialData={props.action === 'edit' && props.schedule?.CTRLOPER === '4_ECO' ? {
              setpoint: numToStr(props.schedule.SETPOINT),
              ltc_degree: numToStr(props.schedule.LTC),
              lti_degree: numToStr(props.schedule.LTI),
              lti_cmd: props.schedule.LTI_CMD?.toString() ?? '',
              upperHysteresis: numToStr(props.schedule.UPPER_HYSTERESIS),
              lowerHysteresis: numToStr(props.schedule.LOWER_HYSTERESIS),
              initialBehaviorProgrammingHour: props.schedule.DEFAULT_COMMAND ?? '',
            } : undefined}
          />
        )
      }
      </Flex>
      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{
          marginTop: '41px',
          marginLeft: '138px',
        }}
      >
        <Button
          data-testid="schedule-dut4-save-btn"
          style={{ maxWidth: '178px' }}
          onClick={(e) => { createOrEditSchedule(); }}
          variant="primary"
        >
          {`${props.cardIndex == null ? t('botaoAdicionar') : t('botaoEditar')}`}
        </Button>
      </Flex>
      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{
          marginTop: '22px',
          marginLeft: '195px',
        }}
      >
        <u data-testid="schedule-dut4-cancel-btn" style={{ color: colors.DarkGrey, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); props.onHandleCancel(); }}>
          {t('botaoCancelar')}
        </u>
      </Flex>
    </Flex>
  );
};
